/**
 * Adaptive Learning Engine
 * ---------------------------------------------------------------
 * Behavioral optimization layer that mines `content_assets`,
 * `content_metrics` and `content_events` to surface winning
 * archetype / escalation / format / hook structures, derive
 * generation biases, and persist intelligence memory into
 * `adaptive_learning_profiles`, `behavioral_rankings`,
 * `hook_intelligence`, and `generation_biases`.
 *
 * NOT analytics. Forensic behavioral signal infrastructure.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Brain, Zap, GitBranch, Layers, Globe2, Sparkles, Database,
  ArrowUpRight, ArrowDownRight, Loader2, Activity,
} from "lucide-react";
import {
  calculateEngagementScore, syntheticBaseline, type AssetMetricsRow,
} from "@/lib/contentMetrics";
import { CONTENT_ASSETS_CHANGED_EVENT } from "@/lib/contentTelemetry";
import RealityCorrectionLayer from "@/components/admin/RealityCorrectionLayer";

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────
interface AssetRow {
  id: string;
  scenario_id: string;
  scenario_title: string;
  archetype: string;
  format: string;
  platform: string;
  hook: string | null;
  category: string;
  escalation_path?: string | null;
  behavioral_intensity?: string | null;
}
interface MergedAsset extends AssetRow {
  metrics: Partial<AssetMetricsRow>;
  engagement: number;
  replay: number;
  saveRatio: number;
  shareVelocity: number;
  commentIntensity: number;
}

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
const titleCase = (s: string) =>
  (s ?? "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const avg = (arr: number[]) =>
  arr.length ? arr.reduce((s, n) => s + n, 0) / arr.length : 0;

const pct = (n: number) => `${Math.round(n)}%`;

function deriveEscalation(a: AssetRow): string {
  if (a.escalation_path) return a.escalation_path.toLowerCase();
  const k = `${a.category ?? ""} ${a.archetype ?? ""}`.toLowerCase();
  if (k.includes("guilt")) return "guilt";
  if (k.includes("withdraw") || k.includes("silent")) return "withdrawal";
  if (k.includes("defens") || k.includes("blame")) return "defensive";
  return "emotional";
}

function replayIntensity(m: Partial<AssetMetricsRow>): number {
  const v = m.views ?? 0, i = m.impressions ?? 0;
  return i > 0 ? Math.min(100, Math.round((v / i) * 100)) : 0;
}
function saveRatio(m: Partial<AssetMetricsRow>): number {
  const v = m.views ?? 0, s = m.saves ?? 0;
  return v > 0 ? Math.min(100, (s / v) * 100) : 0;
}
function shareVelocity(m: Partial<AssetMetricsRow>): number {
  // shares per 100 impressions
  const i = m.impressions ?? 0, s = m.shares ?? 0;
  return i > 0 ? Math.min(100, (s / i) * 100) : 0;
}
function commentIntensity(m: Partial<AssetMetricsRow>): number {
  const v = m.views ?? 0, c = m.comments ?? 0;
  return v > 0 ? Math.min(100, (c / v) * 100) : 0;
}

// ─────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────
const SectionCard = ({
  icon: Icon, title, hint, children,
}: {
  icon: any; title: string; hint?: string; children: React.ReactNode;
}) => (
  <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
    <CardHeader className="pb-3">
      <CardTitle className="text-base flex items-center gap-2">
        <Icon className="w-4 h-4 text-cyan" />
        {title}
        {hint && (
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono ml-1">
            {hint}
          </span>
        )}
      </CardTitle>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

const Bar = ({ value, tone = "cyan" }: { value: number; tone?: "cyan" | "amber" | "emerald" | "red" }) => {
  const map: Record<string, string> = {
    cyan: "bg-cyan", amber: "bg-amber-400", emerald: "bg-emerald-400", red: "bg-red-400",
  };
  return (
    <div className="h-1.5 w-full rounded-full bg-charcoal-deep border border-glass-border overflow-hidden">
      <div className={`h-full ${map[tone]} transition-all duration-700`}
        style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
    </div>
  );
};

const LIVE_TICKERS = [
  "Signal shift detected · replay retention rising",
  "Withdrawal escalation trending upward",
  "Hook compression improving across TikTok cohort",
  "Save ratio drifting into POV format",
  "Comment intensity stabilizing on Spiral structure",
  "Adaptive weights re-balanced",
  "Behavioral memory layer synchronizing",
  "Replay anchor phrase reclassified",
];

// ─────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────
const AdaptiveLearningEngine = () => {
  const [loading, setLoading] = useState(true);
  const [persisting, setPersisting] = useState(false);
  const [assets, setAssets] = useState<MergedAsset[]>([]);
  const [tickerIdx, setTickerIdx] = useState(0);
  const [, force] = useState(0);
  const tickRef = useRef(0);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: a }, { data: m }] = await Promise.all([
      (supabase as any).from("content_assets")
        .select("id, scenario_id, scenario_title, archetype, format, platform, hook, category, escalation_path, behavioral_intensity")
        .order("created_at", { ascending: false }).limit(800),
      (supabase as any).from("content_metrics").select("*").limit(2000),
    ]);
    const mMap = new Map<string, any>();
    (m ?? []).forEach((r: any) => r?.content_asset_id && mMap.set(r.content_asset_id, r));
    const merged: MergedAsset[] = (a ?? []).map((row: AssetRow) => {
      const real = mMap.get(row.id) ?? {};
      const synth = syntheticBaseline(row.id);
      const metrics = {
        ...synth,
        ...Object.fromEntries(Object.entries(real).filter(
          ([, v]) => v !== null && v !== undefined && v !== 0
        )),
      } as Partial<AssetMetricsRow>;
      return {
        ...row,
        metrics,
        engagement: calculateEngagementScore(metrics),
        replay: replayIntensity(metrics),
        saveRatio: saveRatio(metrics),
        shareVelocity: shareVelocity(metrics),
        commentIntensity: commentIntensity(metrics),
      };
    });
    setAssets(merged);
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  // live ticker + pulse
  useEffect(() => {
    const id = window.setInterval(() => {
      tickRef.current += 1;
      setTickerIdx((i) => (i + 1) % LIVE_TICKERS.length);
      force((v) => v + 1);
    }, 3500);
    return () => window.clearInterval(id);
  }, []);

  // periodic refresh
  useEffect(() => {
    const id = window.setInterval(() => { void load(); }, 60_000);
    return () => window.clearInterval(id);
  }, [load]);

  // live refresh on signal injection
  useEffect(() => {
    const handler = () => { void load(); };
    window.addEventListener(CONTENT_ASSETS_CHANGED_EVENT, handler);
    return () => window.removeEventListener(CONTENT_ASSETS_CHANGED_EVENT, handler);
  }, [load]);

  // ── Aggregations ──────────────────────────────────────────
  const winners = useMemo(() => {
    const group = <K extends string>(picker: (a: MergedAsset) => K) => {
      const map = new Map<string, MergedAsset[]>();
      assets.forEach((a) => {
        const k = picker(a);
        if (!k) return;
        if (!map.has(k)) map.set(k, []);
        map.get(k)!.push(a);
      });
      return map;
    };
    const summarize = (m: Map<string, MergedAsset[]>) =>
      Array.from(m.entries()).map(([key, list]) => ({
        key,
        sample: list.length,
        replay: avg(list.map((x) => x.replay)),
        completion: avg(list.map((x) => Number(x.metrics.completion_rate ?? 0))),
        engagement: avg(list.map((x) => x.engagement)),
        saveRatio: avg(list.map((x) => x.saveRatio)),
        shareVelocity: avg(list.map((x) => x.shareVelocity)),
        commentIntensity: avg(list.map((x) => x.commentIntensity)),
      }));

    return {
      archetypes: summarize(group((a) => a.archetype || "unknown")).sort((a, b) => b.engagement - a.engagement),
      escalations: summarize(group((a) => deriveEscalation(a))).sort((a, b) => b.replay - a.replay),
      formats: summarize(group((a) => a.format || "unknown")).sort((a, b) => b.completion - a.completion),
      pacing: summarize(group((a) => (a.behavioral_intensity || "medium").toLowerCase())).sort((a, b) => b.engagement - a.engagement),
    };
  }, [assets]);

  const winnerHighlights = useMemo(() => {
    const top = <T extends { key: string }>(arr: T[], k: keyof T) =>
      [...arr].sort((a, b) => (b[k] as any) - (a[k] as any))[0];
    return {
      replay: top(winners.escalations, "replay"),
      saveRatio: top(winners.archetypes, "saveRatio"),
      completion: top(winners.formats, "completion"),
      comments: top(winners.formats, "commentIntensity"),
    };
  }, [winners]);

  // ── Hook intelligence ────────────────────────────────────
  const hookRows = useMemo(() => {
    const baseRetention = avg(assets.map((a) => Number(a.metrics.completion_rate ?? 0))) || 1;
    const baseReplay = avg(assets.map((a) => a.replay)) || 1;
    const baseComment = avg(assets.map((a) => a.commentIntensity)) || 1;

    return assets
      .filter((a) => a.hook && a.hook.length > 6)
      .map((a) => {
        const replayLift = Math.round(((a.replay - baseReplay) / Math.max(1, baseReplay)) * 100);
        const completionLift = Math.round(
          ((Number(a.metrics.completion_rate ?? 0) - baseRetention) / baseRetention) * 100
        );
        const commentLift = Math.round(((a.commentIntensity - baseComment) / Math.max(1, baseComment)) * 100);
        let classification: "curiosity" | "replay_trigger" | "emotional_opener" | "retention" = "emotional_opener";
        if (replayLift >= 20) classification = "replay_trigger";
        else if (completionLift >= 20) classification = "retention";
        else if ((a.hook ?? "").match(/\?|wait|notice|did you/i)) classification = "curiosity";
        return {
          id: a.id,
          phrase: a.hook!,
          archetype: a.archetype,
          format: a.format,
          platform: a.platform,
          replayLift,
          completionLift,
          commentLift,
          classification,
        };
      })
      .sort((a, b) => b.replayLift - a.replayLift)
      .slice(0, 14);
  }, [assets]);

  // ── Escalation × Format heatmap ──────────────────────────
  const escalationFormatMatrix = useMemo(() => {
    const paths = ["emotional", "defensive", "guilt", "withdrawal"];
    const formats = Array.from(new Set(assets.map((a) => a.format).filter(Boolean)));
    const cells: Record<string, Record<string, { replay: number; completion: number; save: number; engagement: number; sample: number }>> = {};
    paths.forEach((p) => {
      cells[p] = {};
      formats.forEach((f) => {
        const list = assets.filter((a) => deriveEscalation(a) === p && a.format === f);
        cells[p][f] = {
          replay: avg(list.map((x) => x.replay)),
          completion: avg(list.map((x) => Number(x.metrics.completion_rate ?? 0))),
          save: avg(list.map((x) => x.saveRatio)),
          engagement: avg(list.map((x) => x.engagement)),
          sample: list.length,
        };
      });
    });
    return { paths, formats, cells };
  }, [assets]);

  // ── Platform behavioral drift ────────────────────────────
  const platformDrift = useMemo(() => {
    const platforms = ["tiktok", "youtube", "instagram"];
    return platforms.map((p) => {
      const list = assets.filter((a) => (a.platform || "").toLowerCase().includes(p));
      const completion = avg(list.map((x) => Number(x.metrics.completion_rate ?? 0)));
      const replay = avg(list.map((x) => x.replay));
      const save = avg(list.map((x) => x.saveRatio));
      const hookLen = avg(list.map((x) => (x.hook ?? "").length));
      const escalationSpeed = list.length
        ? avg(list.map((x) => (x.behavioral_intensity === "high" ? 100 : x.behavioral_intensity === "low" ? 25 : 60)))
        : 0;
      let optimalPattern = "—";
      if (p === "tiktok") optimalPattern = "Immediate escalation · short hook";
      if (p === "youtube") optimalPattern = "Tension build · deferred reveal";
      if (p === "instagram") optimalPattern = "POV ambiguity · save-driven";
      return {
        platform: p, sample: list.length, completion, replay, save,
        hookLen: Math.round(hookLen), escalationSpeed, optimalPattern,
      };
    });
  }, [assets]);

  // ── Recommended generation biases (auto-derived) ─────────
  const recommendations = useMemo(() => {
    const recs: { dir: "increase" | "reduce"; dim: string; key: string; rationale: string; confidence: number; evidence: number }[] = [];

    // best escalation by replay
    const e = winners.escalations[0];
    if (e) recs.push({
      dir: "increase", dim: "escalation", key: e.key,
      rationale: `${pct(e.replay)} replay retention vs cohort baseline`,
      confidence: Math.min(100, 40 + e.sample * 2), evidence: e.replay,
    });

    // best format by completion
    const f = winners.formats[0];
    if (f) recs.push({
      dir: "increase", dim: "format", key: f.key,
      rationale: `Highest completion · ${pct(f.completion)} across ${f.sample} assets`,
      confidence: Math.min(100, 40 + f.sample * 2), evidence: f.completion,
    });

    // best archetype by saves
    const a = [...winners.archetypes].sort((x, y) => y.saveRatio - x.saveRatio)[0];
    if (a) recs.push({
      dir: "increase", dim: "archetype", key: a.key,
      rationale: `Save ratio ${a.saveRatio.toFixed(1)}% — strongest signal`,
      confidence: Math.min(100, 40 + a.sample * 2), evidence: a.saveRatio,
    });

    // worst format → reduce
    const wf = [...winners.formats].sort((x, y) => x.engagement - y.engagement)[0];
    if (wf && winners.formats.length > 1) recs.push({
      dir: "reduce", dim: "format", key: wf.key,
      rationale: `Underperforming engagement · ${Math.round(wf.engagement)}/100`,
      confidence: Math.min(100, 30 + wf.sample * 2), evidence: 100 - wf.engagement,
    });

    // pacing
    const pacing = [...winners.pacing].sort((x, y) => y.engagement - x.engagement)[0];
    if (pacing) recs.push({
      dir: "increase", dim: "pacing", key: pacing.key,
      rationale: `Behavioral intensity tier dominating engagement`,
      confidence: Math.min(100, 35 + pacing.sample * 2), evidence: pacing.engagement,
    });

    // soft hooks
    const softHook = hookRows.find((h) => h.replayLift < -10);
    if (softHook) recs.push({
      dir: "reduce", dim: "hook", key: softHook.classification,
      rationale: `Soft openers underperform replay baseline by ${Math.abs(softHook.replayLift)}%`,
      confidence: 60, evidence: Math.abs(softHook.replayLift),
    });

    return recs.slice(0, 8);
  }, [winners, hookRows]);

  // ── Behavioral combination rankings ──────────────────────
  const combinations = useMemo(() => {
    const map = new Map<string, MergedAsset[]>();
    assets.forEach((a) => {
      const k = `${a.archetype || "—"}|${deriveEscalation(a)}|${a.format || "—"}`;
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(a);
    });
    return Array.from(map.entries())
      .map(([k, list]) => {
        const [arch, esc, fmt] = k.split("|");
        const replay = avg(list.map((x) => x.replay));
        const completion = avg(list.map((x) => Number(x.metrics.completion_rate ?? 0)));
        const save = avg(list.map((x) => x.saveRatio));
        const comments = avg(list.map((x) => x.commentIntensity));
        const shares = avg(list.map((x) => x.shareVelocity));
        const composite = Math.round(replay * 0.25 + completion * 0.25 + save * 0.2 + comments * 0.15 + shares * 0.15);
        return { archetype: arch, escalation: esc, format: fmt, sample: list.length, replay, completion, save, comments, shares, composite };
      })
      .filter((r) => r.sample >= 1)
      .sort((a, b) => b.composite - a.composite)
      .slice(0, 8);
  }, [assets]);

  // ── Persist intelligence memory ──────────────────────────
  const persistMemory = useCallback(async () => {
    setPersisting(true);
    try {
      // 0) Determine next cycle number + load previous cycle for delta math
      const { data: lastCycleRows } = await (supabase as any)
        .from("learning_cycles")
        .select("cycle_number, summary")
        .order("cycle_number", { ascending: false })
        .limit(1);
      const prev = lastCycleRows?.[0];
      const nextCycleNumber = (prev?.cycle_number ?? 0) + 1;

      // 1) Build current-cycle summary (averages used for deltas)
      const currentSummary = {
        avg_engagement: avg(assets.map((a) => a.engagement)),
        avg_replay: avg(assets.map((a) => a.replay)),
        avg_completion: avg(assets.map((a) => Number(a.metrics.completion_rate ?? 0))),
        avg_save_ratio: avg(assets.map((a) => a.saveRatio)),
        avg_share_velocity: avg(assets.map((a) => a.shareVelocity)),
        top_archetype: winners.archetypes[0]?.key ?? null,
        top_format: winners.formats[0]?.key ?? null,
        top_escalation: winners.escalations[0]?.key ?? null,
        sample_size: assets.length,
      };

      // 2) Compute deltas vs previous cycle (impact narratives)
      const prevSum = (prev?.summary ?? {}) as any;
      const delta = (a: number, b: number) =>
        Number.isFinite(b) && b > 0 ? Math.round(((a - b) / b) * 100) : 0;
      const deltas = [
        { label: "Engagement",     delta: delta(currentSummary.avg_engagement, prevSum.avg_engagement ?? 0) },
        { label: "Replay",         delta: delta(currentSummary.avg_replay, prevSum.avg_replay ?? 0) },
        { label: "Completion",     delta: delta(currentSummary.avg_completion, prevSum.avg_completion ?? 0) },
        { label: "Save Ratio",     delta: delta(currentSummary.avg_save_ratio, prevSum.avg_save_ratio ?? 0) },
        { label: "Share Velocity", delta: delta(currentSummary.avg_share_velocity, prevSum.avg_share_velocity ?? 0) },
      ];

      // 3) Insert the cycle row first so downstream rows can FK to it
      const { data: cycleRow, error: cycleErr } = await (supabase as any)
        .from("learning_cycles")
        .insert({
          cycle_number: nextCycleNumber,
          asset_count: assets.length,
          profile_count:
            winners.archetypes.length + winners.formats.length +
            winners.escalations.length + winners.pacing.length,
          bias_count: recommendations.length,
          ranking_count: combinations.length,
          hook_count: hookRows.length,
          summary: currentSummary as any,
          deltas: deltas as any,
        })
        .select("id, cycle_number")
        .single();
      if (cycleErr) throw cycleErr;
      const cycleId = cycleRow.id as string;

      // 4) adaptive_learning_profiles — upsert per dimension/key, attach cycle
      const profiles: any[] = [];
      const pushProfiles = (dim: string, rows: any[]) =>
        rows.forEach((r) => profiles.push({
          dimension: dim, key: r.key,
          weight: 1 + r.engagement / 100,
          rolling_score: r.engagement,
          trend_delta: 0,
          sample_size: r.sample,
          metadata: { replay: r.replay, completion: r.completion, save_ratio: r.saveRatio },
          cycle_id: cycleId,
        }));
      pushProfiles("archetype", winners.archetypes);
      pushProfiles("format", winners.formats);
      pushProfiles("escalation", winners.escalations);
      pushProfiles("pacing", winners.pacing);

      if (profiles.length) {
        await (supabase as any).from("adaptive_learning_profiles").upsert(profiles, {
          onConflict: "dimension,key",
        });
      }

      // 5) behavioral_rankings — fresh snapshot tagged to cycle
      if (combinations.length) {
        const rankings = combinations.map((c, i) => ({
          archetype: c.archetype, escalation_path: c.escalation, format: c.format,
          composite_score: c.composite, replay_score: c.replay, completion_score: c.completion,
          save_score: c.save, comment_score: c.comments, share_velocity: c.shares,
          sample_size: c.sample, rank_position: i + 1,
          cycle_id: cycleId,
        }));
        await (supabase as any).from("behavioral_rankings").insert(rankings);
      }

      // 6) hook_intelligence
      if (hookRows.length) {
        const hooks = hookRows.map((h) => ({
          phrase: h.phrase, archetype: h.archetype, format: h.format, platform: h.platform,
          replay_lift: h.replayLift, completion_lift: h.completionLift, comment_lift: h.commentLift,
          retention_lift: (h.replayLift + h.completionLift) / 2,
          sample_size: 1, classification: h.classification,
          cycle_id: cycleId,
        }));
        await (supabase as any).from("hook_intelligence").insert(hooks);
      }

      // 7) generation_biases — deactivate previous, insert new active set
      //    These are the ACTIVE generation modifiers that future
      //    Generate Intelligence Batch runs will pick up.
      await (supabase as any)
        .from("generation_biases")
        .update({ active: false, updated_at: new Date().toISOString() })
        .eq("active", true);

      if (recommendations.length) {
        const biases = recommendations.map((r) => {
          // Normalized weight 0..1 derived from confidence + evidence
          const norm = Math.max(0, Math.min(1,
            (r.confidence / 100) * 0.6 + (Math.min(100, r.evidence) / 100) * 0.4
          ));
          return {
            bias_direction: r.dir,
            dimension: r.dim,
            key: r.key,
            rationale: r.rationale,
            confidence: r.confidence,
            evidence_score: r.evidence,
            weight: Number(norm.toFixed(3)),
            impact_score: Math.round(norm * 100),
            active: true,
            cycle_id: cycleId,
          };
        });
        await (supabase as any).from("generation_biases").insert(biases);
      }

      // 8) Audit + downstream notification
      await (supabase as any).from("content_events").insert({
        content_asset_id: null,
        event_type: "intelligence_committed",
        metadata: {
          cycle_number: nextCycleNumber,
          cycle_id: cycleId,
          asset_count: assets.length,
          bias_count: recommendations.length,
          deltas,
        } as any,
      });

      window.dispatchEvent(new CustomEvent("intelligence-committed", {
        detail: { cycleNumber: nextCycleNumber, cycleId },
      }));
    } catch (err) {
      console.error("[AdaptiveLearningEngine] persist failed:", err);
    } finally {
      setPersisting(false);
    }
  }, [winners, combinations, hookRows, recommendations, assets]);

  // ──────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Card className="bg-charcoal-medium/60 border-glass-border">
        <CardContent className="py-12 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-cyan" />
        </CardContent>
      </Card>
    );
  }

  if (assets.length === 0) {
    return (
      <Card className="bg-charcoal-medium/60 border-glass-border">
        <CardContent className="py-12 text-center text-xs text-muted-foreground font-mono uppercase tracking-wider">
          Adaptive Learning Engine idle · awaiting behavioral signal corpus
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Engine header + live ticker */}
      <Card className="bg-gradient-to-br from-charcoal-deep to-charcoal-medium border-cyan/20">
        <CardContent className="py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Brain className="w-5 h-5 text-cyan" />
              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-cyan animate-ping opacity-70" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-cyan/80 font-mono">
                Adaptive Learning Engine
              </div>
              <div className="text-xs text-muted-foreground font-mono">
                {LIVE_TICKERS[tickerIdx]}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="border-cyan/40 text-cyan font-mono text-[10px]">
              <Activity className="w-3 h-3 mr-1" />
              {assets.length} signals indexed
            </Badge>
            <Button
              variant="outline" size="sm"
              onClick={() => void persistMemory()}
              disabled={persisting}
              className="border-cyan/40 text-cyan hover:bg-cyan/10 font-mono text-[10px] uppercase tracking-wider"
            >
              {persisting ? (
                <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />Writing memory</>
              ) : (
                <><Database className="w-3 h-3 mr-1.5" />Commit Intelligence</>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* SECTION 1 — Behavioral Winners */}
      <SectionCard icon={Zap} title="Behavioral Winners" hint="Top performing structures">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          {[
            { label: "Highest replay retention", val: winnerHighlights.replay, tag: "replay" as const, dim: "Escalation", suffix: "%" },
            { label: "Highest save ratio", val: winnerHighlights.saveRatio, tag: "saveRatio" as const, dim: "Archetype", suffix: "%" },
            { label: "Highest completion", val: winnerHighlights.completion, tag: "completion" as const, dim: "Format", suffix: "%" },
            { label: "Highest comment intensity", val: winnerHighlights.comments, tag: "commentIntensity" as const, dim: "Format", suffix: "%" },
          ].map((h, i) => {
            const pulse = (tickRef.current + i) % 4 === 0;
            const v: any = h.val;
            return (
              <div key={h.label} className="rounded-lg border border-glass-border bg-charcoal-deep/50 p-3">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
                  {h.label}
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <div className="text-sm text-foreground font-semibold">
                    → {titleCase(v?.key ?? "—")}
                    <span className="ml-2 text-[10px] uppercase tracking-wider text-muted-foreground font-mono">
                      {h.dim}
                    </span>
                  </div>
                  <span className={`font-mono text-sm ${pulse ? "text-cyan" : "text-amber-300"} transition-colors`}>
                    {v ? Math.round(v[h.tag]) : 0}{h.suffix}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[
            { title: "Archetypes", rows: winners.archetypes.slice(0, 5), metric: "engagement" as const, label: "Engagement" },
            { title: "Escalation paths", rows: winners.escalations.slice(0, 5), metric: "replay" as const, label: "Replay" },
            { title: "Formats", rows: winners.formats.slice(0, 5), metric: "completion" as const, label: "Completion" },
            { title: "Pacing tiers", rows: winners.pacing.slice(0, 5), metric: "engagement" as const, label: "Engagement" },
          ].map((blk) => (
            <div key={blk.title} className="rounded-lg border border-glass-border bg-charcoal-deep/40 p-3">
              <div className="text-[10px] uppercase tracking-[0.18em] text-cyan/80 font-mono mb-2">
                {blk.title}
              </div>
              <div className="space-y-2">
                {blk.rows.length === 0 && (
                  <div className="text-[11px] text-muted-foreground font-mono">No data yet</div>
                )}
                {blk.rows.map((r) => (
                  <div key={r.key}>
                    <div className="flex items-center justify-between text-[11px] font-mono mb-0.5">
                      <span className="text-foreground uppercase tracking-wider truncate max-w-[60%]">
                        {titleCase(r.key)}
                      </span>
                      <span className="text-muted-foreground">
                        {blk.label} {Math.round((r as any)[blk.metric])}
                      </span>
                    </div>
                    <Bar value={(r as any)[blk.metric]} tone="cyan" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* SECTION 2 — Hook Pattern Intelligence */}
      <SectionCard icon={Sparkles} title="Hook Pattern Intelligence" hint="Opener phrase classification">
        <ScrollArea className="max-h-[400px]">
          <div className="divide-y divide-glass-border">
            {hookRows.map((h, i) => {
              const flash = (tickRef.current + i) % 6 === 0;
              const tone =
                h.classification === "replay_trigger" ? "amber" :
                h.classification === "retention" ? "emerald" :
                h.classification === "curiosity" ? "cyan" : "cyan";
              return (
                <div key={h.id} className="py-2.5 flex items-start gap-3">
                  <Badge variant="outline" className={`text-[9px] font-mono uppercase border-cyan/40 text-cyan/90`}>
                    {h.classification.replace("_", " ")}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-foreground italic line-clamp-2">"{h.phrase}"</div>
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-[10px] uppercase tracking-wider font-mono">
                      <span className="text-muted-foreground">{titleCase(h.archetype)} · {titleCase(h.format)} · {titleCase(h.platform)}</span>
                      <span className={`${flash ? "text-amber-200" : "text-amber-300"} transition-colors`}>
                        Replay {h.replayLift >= 0 ? "+" : ""}{h.replayLift}%
                      </span>
                      <span className={h.completionLift >= 0 ? "text-emerald-300" : "text-red-300"}>
                        Completion {h.completionLift >= 0 ? "+" : ""}{h.completionLift}%
                      </span>
                      <span className={h.commentLift >= 0 ? "text-cyan" : "text-muted-foreground"}>
                        Comments {h.commentLift >= 0 ? "+" : ""}{h.commentLift}%
                      </span>
                    </div>
                  </div>
                  <Bar value={Math.max(0, Math.min(100, 50 + h.replayLift))} tone={tone as any} />
                </div>
              );
            })}
          </div>
        </ScrollArea>
      </SectionCard>

      {/* SECTION 3 — Escalation × Format Performance Matrix */}
      <SectionCard icon={GitBranch} title="Escalation Performance Matrix" hint="Path × format heatmap">
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] font-mono">
            <thead>
              <tr className="border-b border-glass-border">
                <th className="text-left p-2 text-muted-foreground uppercase tracking-wider">Escalation</th>
                {escalationFormatMatrix.formats.map((f) => (
                  <th key={f} className="text-center p-2 text-muted-foreground uppercase tracking-wider">{titleCase(f)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {escalationFormatMatrix.paths.map((p) => (
                <tr key={p} className="border-b border-glass-border/50">
                  <td className="p-2 uppercase tracking-wider text-cyan">{titleCase(p)}</td>
                  {escalationFormatMatrix.formats.map((f) => {
                    const c = escalationFormatMatrix.cells[p][f];
                    const intensity = Math.min(1, c.engagement / 100);
                    return (
                      <td key={f} className="p-2 text-center"
                        style={{ backgroundColor: `hsl(184 100% 50% / ${intensity * 0.18})` }}>
                        {c.sample === 0 ? (
                          <span className="text-muted-foreground/50">—</span>
                        ) : (
                          <div className="space-y-0.5">
                            <div className="text-foreground">{Math.round(c.engagement)}</div>
                            <div className="text-[9px] text-amber-300">R {Math.round(c.replay)}%</div>
                            <div className="text-[9px] text-emerald-300">S {c.save.toFixed(1)}%</div>
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* SECTION 4 — Format Learning Layer */}
      <SectionCard icon={Layers} title="Format Learning Layer" hint="Adaptive format ranking">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {winners.formats.map((f, i) => (
            <div key={f.key} className="rounded-lg border border-glass-border bg-charcoal-deep/50 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold uppercase tracking-wider text-foreground">{titleCase(f.key)}</div>
                <Badge variant="outline" className="text-[9px] font-mono border-cyan/40 text-cyan">
                  RANK #{i + 1}
                </Badge>
              </div>
              {[
                { label: "Completion", v: f.completion, t: "emerald" as const },
                { label: "Replay", v: f.replay, t: "amber" as const },
                { label: "Comments", v: f.commentIntensity, t: "cyan" as const },
                { label: "Save Ratio", v: f.saveRatio, t: "emerald" as const },
                { label: "Share Velocity", v: f.shareVelocity, t: "cyan" as const },
              ].map((m) => (
                <div key={m.label}>
                  <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider mb-0.5">
                    <span className="text-muted-foreground">{m.label}</span>
                    <span className="text-foreground">{Math.round(m.v)}</span>
                  </div>
                  <Bar value={m.v} tone={m.t} />
                </div>
              ))}
            </div>
          ))}
        </div>
      </SectionCard>

      {/* SECTION 5 — Platform Behavioral Drift */}
      <SectionCard icon={Globe2} title="Platform Behavioral Drift" hint="Cross-platform pattern mapping">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {platformDrift.map((p) => (
            <div key={p.platform} className="rounded-lg border border-glass-border bg-charcoal-deep/50 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold uppercase tracking-wider text-foreground">{titleCase(p.platform)}</div>
                <Badge variant="outline" className="text-[9px] font-mono border-cyan/40 text-cyan">
                  {p.sample} assets
                </Badge>
              </div>
              <div className="text-[11px] text-muted-foreground italic line-clamp-2">{p.optimalPattern}</div>
              <div className="grid grid-cols-2 gap-2 text-[10px] font-mono uppercase tracking-wider pt-1 border-t border-glass-border">
                <div>
                  <div className="text-muted-foreground">Replay</div>
                  <div className="text-amber-300">{Math.round(p.replay)}%</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Completion</div>
                  <div className="text-emerald-300">{Math.round(p.completion)}%</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Save</div>
                  <div className="text-cyan">{p.save.toFixed(1)}%</div>
                </div>
                <div>
                  <div className="text-muted-foreground">Hook Length</div>
                  <div className="text-foreground">{p.hookLen} ch</div>
                </div>
                <div className="col-span-2">
                  <div className="text-muted-foreground">Escalation Speed</div>
                  <Bar value={p.escalationSpeed} tone="cyan" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* SECTION 6 — Recommended Generation Biases */}
      <SectionCard icon={ArrowUpRight} title="Recommended Generation Biases" hint="Auto-derived adaptive directives">
        <div className="space-y-2">
          {recommendations.length === 0 && (
            <div className="text-[11px] text-muted-foreground font-mono uppercase tracking-wider">
              Insufficient signal density · awaiting more samples
            </div>
          )}
          {recommendations.map((r, i) => {
            const Icon = r.dir === "increase" ? ArrowUpRight : ArrowDownRight;
            const tone = r.dir === "increase" ? "text-emerald-300 border-emerald-400/40 bg-emerald-400/5" : "text-red-300 border-red-400/40 bg-red-400/5";
            return (
              <div key={i} className={`rounded-lg border ${tone} p-3 flex items-start gap-3`}>
                <Icon className="w-4 h-4 mt-0.5" />
                <div className="flex-1">
                  <div className="text-sm font-semibold uppercase tracking-wider">
                    {r.dir} · {titleCase(r.dim)} · {titleCase(r.key)}
                  </div>
                  <div className="text-[11px] text-muted-foreground font-mono mt-0.5">{r.rationale}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-mono">Confidence</div>
                  <div className="text-sm font-mono text-foreground">{r.confidence}%</div>
                </div>
              </div>
            );
          })}
        </div>
      </SectionCard>

      {/* SECTION 7 — Persistent Behavioral Memory */}
      <SectionCard icon={Database} title="Persistent Behavioral Memory" hint="Winning combination intelligence">
        <div className="space-y-2">
          {combinations.map((c, i) => (
            <div key={i} className="rounded-lg border border-glass-border bg-charcoal-deep/40 p-3 flex items-center gap-3">
              <Badge variant="outline" className="text-[9px] font-mono border-cyan/40 text-cyan shrink-0">
                #{i + 1}
              </Badge>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-foreground uppercase tracking-wider font-semibold truncate">
                  {titleCase(c.archetype)} → {titleCase(c.escalation)} → {titleCase(c.format)}
                </div>
                <div className="flex flex-wrap gap-3 text-[10px] font-mono uppercase tracking-wider mt-1">
                  <span className="text-amber-300">Replay {Math.round(c.replay)}%</span>
                  <span className="text-emerald-300">Completion {Math.round(c.completion)}%</span>
                  <span className="text-cyan">Save {c.save.toFixed(1)}%</span>
                  <span className="text-muted-foreground">Sample {c.sample}</span>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-mono">Composite</div>
                <div className="text-base font-bold text-cyan font-mono">{c.composite}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70 font-mono text-center">
          Memory persists to adaptive_learning_profiles · behavioral_rankings · hook_intelligence · generation_biases
        </div>
      </SectionCard>

      {/* ─── Reality Correction Layer ───────────────────────── */}
      <RealityCorrectionLayer />
    </div>
  );
};

export default AdaptiveLearningEngine;
